/* Exact supplied split headings and Cookie lettering, measured in millimeters. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory(require('./je-family-model.js'));
  else root.FamilyRenderer = factory(root.FamilyModel);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (M) {
  'use strict';
  const EM = 512, DPI = 600;
  function canvas(width, height) { const c = document.createElement('canvas'); c.width = Math.ceil(width); c.height = Math.ceil(height); return c; }
  function timeout(promise, label) {
    let timer;
    return Promise.race([promise, new Promise((_, reject) => { timer = setTimeout(() => reject(new Error(label + ' took too long to load. Retry loading.')), 15000); })]).finally(() => clearTimeout(timer));
  }
  async function load(options = {}) {
    if (!M) throw new Error('Family editor files did not load. Reload this page to retry.');
    const base = options.baseUrl || '/assets/', font = M.FONTS[M.FONT_ID], fontFamily = font.cssFamily, icons = {}, sourceImages = {};
    function assetUrl(value, file) {
      const raw = typeof value === 'string' ? value : value?.url;
      const url = new URL(raw || base + file, document.baseURI);
      if (!['http:', 'https:'].includes(url.protocol)) throw new TypeError('Use an HTTP asset URL.');
      return url.href;
    }
    const metrics = new Map(), layoutCache = new Map(), obstacleCache = new Map();
    const face = new FontFace(fontFamily, 'url(' + JSON.stringify(assetUrl(options.fonts?.[M.FONT_ID], font.file)) + ')' , { weight: '400', style: 'normal' });
    try { await timeout(face.load(), 'Cookie'); document.fonts.add(face); }
    catch (_) { throw new Error('Could not load Cookie. Retry loading to use the exact supplied font.'); }
    await Promise.all(Object.values(M.TEMPLATES).filter(spec => !options.templateFamily || spec.family === options.templateFamily).map(async spec => {
      const image = new Image();
      await timeout(new Promise((resolve, reject) => {
        image.onload = resolve; image.onerror = () => reject(new Error('Could not load the supplied ' + spec.label + ' artwork. Retry loading.')); image.crossOrigin = 'anonymous'; image.src = assetUrl(options.icons?.[spec.id], spec.file);
      }), spec.label + ' artwork');
      if (image.naturalWidth !== spec.imageWidth || image.naturalHeight !== spec.imageHeight) throw new Error('The supplied ' + spec.label + ' artwork dimensions have changed.');
      // The asset preparation preserves source alpha. Rotate once without
      // resampling, so every heading remains the original supplied artwork.
      const rotated = canvas(spec.imageHeight, spec.imageWidth), ctx = rotated.getContext('2d', { willReadFrequently: true });
      ctx.translate(0, spec.imageWidth); ctx.rotate(-Math.PI / 2); ctx.drawImage(image, 0, 0); icons[spec.id] = rotated; sourceImages[spec.id] = image;
    }));
    function fontStyle(ctx) {
      ctx.font = EM + 'px "' + fontFamily + '"'; ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
      ctx.fontKerning = 'normal'; ctx.fillStyle = '#000';
    }
    function measure(text, fontId = M.FONT_ID, angle = 0) {
      if (fontId !== M.FONT_ID || typeof text !== 'string' || !text.trim() || text.length > M.MAX_NAME_LENGTH || M.unsupportedCharacters(text).length) throw new RangeError('Enter names supported by the supplied Cookie font.');
      if (![0, -90].includes(angle)) throw new RangeError('The family lettering orientation is invalid.');
      const key = angle + '\0' + text;
      if (metrics.has(key)) return metrics.get(key);
      const ctx = canvas(1, 1).getContext('2d'); fontStyle(ctx); const measured = ctx.measureText(text);
      let xMin = -measured.actualBoundingBoxLeft, xMax = measured.actualBoundingBoxRight, yMin = -measured.actualBoundingBoxAscent, yMax = measured.actualBoundingBoxDescent;
      if (![xMin, xMax, yMin, yMax].every(Number.isFinite) || !(xMax > xMin && yMax > yMin)) throw new RangeError('Each name needs visible lettering.');
      const ox = Math.floor(xMin) - 4, oy = Math.floor(yMin) - 4, width = Math.ceil(xMax) - ox + 4, height = Math.ceil(yMax) - oy + 4;
      const resolution = Math.min(4, 8192 / width, 4096 / height, Math.sqrt(8000000 / (width * height)));
      const probe = canvas(width * resolution, height * resolution), probeCtx = probe.getContext('2d', { willReadFrequently: true });
      probeCtx.scale(resolution, resolution); fontStyle(probeCtx); probeCtx.fillText(text, -ox, -oy);
      const pixels = probeCtx.getImageData(0, 0, probe.width, probe.height).data;
      let left = probe.width, right = -1, top = probe.height, bottom = -1;
      for (let i = 3; i < pixels.length; i += 4) if (pixels[i] >= 32) {
        const p = (i - 3) / 4, x = p % probe.width, y = Math.floor(p / probe.width);
        left = Math.min(left, x); right = Math.max(right, x); top = Math.min(top, y); bottom = Math.max(bottom, y);
      }
      if (right < left || bottom < top) throw new RangeError('Each name needs visible lettering.');
      xMin = ox + left / resolution; xMax = ox + (right + 1) / resolution; yMin = oy + top / resolution; yMax = oy + (bottom + 1) / resolution;
      const horizontalWidth = xMax - xMin, horizontalHeight = yMax - yMin;
      const result = Object.freeze({ text, fontId, angle, xMin, xMax, yMin, yMax, advance: measured.width, horizontalWidth, horizontalHeight,
        width: angle ? horizontalHeight : horizontalWidth, height: angle ? horizontalWidth : horizontalHeight,
        aspect: angle ? horizontalHeight / horizontalWidth : horizontalWidth / horizontalHeight });
      if (metrics.size >= 400) metrics.clear(); metrics.set(key, result); return result;
    }
    function remember(cache, key, value, limit) {
      if (cache.size >= limit) cache.delete(cache.keys().next().value);
      cache.set(key, value); return value;
    }
    function titleObstacles(spec) {
      if (obstacleCache.has(spec.id)) return obstacleCache.get(spec.id);
      const source = canvas(spec.imageWidth, spec.imageHeight), ctx = source.getContext('2d', { willReadFrequently: true });
      ctx.drawImage(sourceImages[spec.id], 0, 0);
      const pixels = ctx.getImageData(0, 0, source.width, source.height).data;
      const stride = source.width + 1, sums = new Uint32Array(stride * (source.height + 1));
      for (let y = 0; y < source.height; y++) {
        let row = 0;
        for (let x = 0; x < source.width; x++) {
          // The user's continuous divider stays in the artwork. Only that
          // verified narrow slab may cross a tail; letter strokes never may.
          if (!(y >= spec.divider.top && y < spec.divider.bottom) && pixels[(y * source.width + x) * 4 + 3] >= 16) row++;
          sums[(y + 1) * stride + x + 1] = sums[y * stride + x + 1] + row;
        }
      }
      return remember(obstacleCache, spec.id, { sums, stride, width: source.width, height: source.height }, 4);
    }
    function descenderRuns(m, scale) {
      const ox = Math.floor(m.xMin * scale) - 2, oy = Math.floor(m.yMin * scale) - 2;
      const probe = canvas(Math.ceil(m.xMax * scale) - ox + 2, Math.ceil(m.yMax * scale) - oy + 2);
      const ctx = probe.getContext('2d', { willReadFrequently: true });
      ctx.scale(scale, scale); fontStyle(ctx); ctx.fillText(m.text, -ox / scale, -oy / scale);
      const pixels = ctx.getImageData(0, 0, probe.width, probe.height).data, runs = [];
      // The entire upper body is contained in the blank band. Testing the
      // actual lower ink keeps the search small even for long names.
      for (let y = Math.max(0, Math.floor(-oy) - 1); y < probe.height; y++) {
        let start = -1;
        for (let x = 0; x <= probe.width; x++) {
          const ink = x < probe.width && pixels[(y * probe.width + x) * 4 + 3] >= 16;
          if (ink && start < 0) start = x;
          else if (!ink && start >= 0) {
            runs.push([ox - m.xMin * scale + start, ox - m.xMin * scale + x, oy + y]); start = -1;
          }
        }
      }
      return runs;
    }
    function fitNames(spec, names, separator = 'none') {
      const key = JSON.stringify([spec.id, names, separator]);
      if (layoutCache.has(key)) return layoutCache.get(key);
      const measured = names.map(name => measure(name)), ctx = canvas(1, 1).getContext('2d'); fontStyle(ctx);
      const opening = spec.opening, count = names.length, heartUnits = separator === 'heart' && count > 1 ? EM * .14 : 0;
      // Reserve the complete separator plus breathing room before fitting the
      // names. The none branch retains the released equations exactly.
      const minimumGap = Math.max(ctx.measureText('  ').width, heartUnits ? heartUnits + EM * .16 : 0);
      const totalWidth = measured.reduce((sum, m) => sum + m.width, 0);
      const yMin = Math.min(...measured.map(m => m.yMin)), yMax = Math.max(...measured.map(m => m.yMax));
      const widthScale = opening.width * .94 / (totalWidth + minimumGap * (count - 1));
      const baseScale = Math.min(widthScale, opening.height * .96 / (yMax - yMin));
      function balanced(scale) {
        const spare = Math.max(0, opening.width * .94 - (totalWidth + minimumGap * (count - 1)) * scale);
        const breathingRoom = spare / (count + 1), gap = minimumGap * scale + breathingRoom;
        let cursor = opening.x + opening.width * .03 + breathingRoom;
        const xs = measured.map(m => { const x = cursor; cursor += m.width * scale + gap; return x; });
        return { xs, gap };
      }
      function hearts(scale, xs) {
        if (!heartUnits || count < 2) return [];
        const size = Math.min(heartUnits * scale, opening.height * .26);
        return measured.slice(1).map((_, index) => {
          const left = xs[index] + measured[index].width * scale, right = xs[index + 1];
          return { x: (left + right - size) / 2, y: opening.y + (opening.height - size) / 2, width: size, height: size };
        });
      }
      function result(scale, baseline, xs) {
        return {
          names: measured.map((m, i) => ({ x: xs[i], y: baseline + m.yMin * scale, width: m.width * scale, height: m.height * scale })),
          hearts: hearts(scale, xs),
          fontGain: scale / baseScale, descenderOptimized: scale > baseScale * 1.001
        };
      }
      const baseXs = balanced(baseScale).xs, baseY = opening.y + (opening.height - (yMax - yMin) * baseScale) / 2;
      const fallback = { names: measured.map((m, i) => ({ x: baseXs[i], y: baseY + (m.yMin - yMin) * baseScale,
        width: m.width * baseScale, height: m.height * baseScale })), hearts: hearts(baseScale, baseXs), fontGain: 1, descenderOptimized: false };
      // Ordinary names and width-limited lists retain the existing fit exactly.
      // A real descender is needed before the body-height cap is appropriate.
      const padding = opening.height * .02;
      const maximumScale = Math.min(widthScale, (opening.height - 2 * padding) / -yMin,
        (spec.imageHeight - opening.y - 2 * padding) / (yMax - yMin));
      if (!spec.divider || yMax <= Math.max(12, -yMin * .12) || maximumScale < baseScale * 1.015) return remember(layoutCache, key, fallback, 160);
      const obstacle = titleObstacles(spec);
      let budget = 800000;
      function collides(runs, x, baseline) {
        const { sums, stride, width, height } = obstacle, clearance = 1.25;
        for (const run of runs) {
          if (--budget < 0) return true;
          const left = Math.max(0, Math.floor(x + run[0] - clearance)), right = Math.min(width, Math.ceil(x + run[1] + clearance));
          const top = Math.max(0, Math.floor(baseline + run[2] - clearance)), bottom = Math.min(height, Math.ceil(baseline + run[2] + 1 + clearance));
          if (sums[bottom * stride + right] - sums[top * stride + right] - sums[bottom * stride + left] + sums[top * stride + left]) return true;
        }
        return false;
      }
      function atScale(scale) {
        const ideal = balanced(scale), widths = measured.map(m => m.width * scale), minimum = minimumGap * scale;
        const maxShift = Math.min(opening.width * .045, ideal.gap * .32);
        const lowBaseline = opening.y + padding - yMin * scale;
        const highBaseline = Math.min(opening.y + opening.height - padding, spec.imageHeight - padding - yMax * scale);
        if (highBaseline < lowBaseline - 1e-6) return null;
        const runs = measured.map(m => descenderRuns(m, scale));
        const baselines = [...new Set([1, 0, .75, .25, .5].map(t => lowBaseline + (highBaseline - lowBaseline) * t))];
        let best = null;
        for (const baseline of baselines) {
          const choices = widths.map((width, i) => {
            const candidates = [];
            for (let step = -8; step <= 8; step++) {
              const x = ideal.xs[i] + maxShift * step / 8;
              if (x < opening.x + opening.width * .03 - 1e-6 || x + width > opening.x + opening.width * .97 + 1e-6) continue;
              if (!collides(runs[i], x, baseline)) candidates.push({ x, cost: Math.pow((x - ideal.xs[i]) / Math.max(1, maxShift), 2) });
            }
            return candidates;
          });
          if (choices.some(list => !list.length)) continue;
          // Dynamic programming finds the most balanced ordered combination;
          // moving one name cannot squeeze or strand its neighbour.
          let states = choices[0].map(p => ({ x: p.x, score: p.cost, xs: [p.x] }));
          for (let i = 1; i < count && states.length; i++) {
            const next = [];
            for (const p of choices[i]) {
              let chosen = null;
              for (const state of states) {
                const gap = p.x - state.x - widths[i - 1];
                if (gap < Math.max(minimum, ideal.gap * .55) - 1e-6 || gap > ideal.gap * 1.5 + 1e-6) continue;
                const score = state.score + p.cost + Math.pow((gap - ideal.gap) / Math.max(1, maxShift), 2);
                if (!chosen || score < chosen.score) chosen = { x: p.x, score, xs: [...state.xs, p.x] };
              }
              if (chosen) next.push(chosen);
            }
            states = next;
          }
          for (const state of states) if (!best || state.score < best.score) best = { ...state, baseline, scale };
          if (budget < 0) break;
        }
        return best;
      }
      let chosen = null, previous = maximumScale;
      // Feasibility is not monotonic: a tail can pass through different letter
      // openings at different sizes. Sample downward, never binary-search.
      for (let step = 0; step < 14 && budget > 0; step++) {
        const scale = maximumScale - (maximumScale - baseScale) * step / 14;
        const candidate = atScale(scale);
        if (candidate) {
          chosen = candidate;
          for (let refinement = 1; step && refinement <= 3 && budget > 0; refinement++) {
            const refined = atScale(previous - (previous - scale) * refinement / 4);
            if (refined) { chosen = refined; break; }
          }
          break;
        }
        previous = scale;
      }
      return remember(layoutCache, key, chosen ? result(chosen.scale, chosen.baseline, chosen.xs) : fallback, 160);
    }
    function createDesign(seed = {}, settings = {}) {
      if (seed.fontId !== undefined && seed.fontId !== M.FONT_ID) throw new RangeError('Family names use Cookie. Choose the Cookie family design before continuing.');
      if (seed.fontEdition !== undefined && seed.fontEdition !== M.FONT_EDITION) throw new RangeError('This design uses an unsupported family lettering version.');
      if (seed.placementEdition !== undefined && seed.placementEdition !== M.STANLEY_PANEL_EDITION) throw new RangeError('This design uses an unsupported family placement version.');
      const separator = M.separatorFor(seed);
      if (!['none', 'heart'].includes(separator)) throw new RangeError('Choose no separators or hearts between names.');
      const templateFamily = seed.templateFamily || 'mom', variant = String(seed.variant || (templateFamily === 'grandma' ? 'grandma' : 'mom'));
      const spec = M.variantFor({ templateFamily, variant }); if (!spec) throw new RangeError('Choose one of the supplied MOM or Grandma templates.');
      if (!icons[spec.id]) throw new RangeError('This family title is not loaded. Reload the matching product page.');
      const productId = seed.productId || 'owala'; if (!Object.hasOwn(M.PRODUCTS, productId)) throw new RangeError('Choose Owala or Stanley.');
      const sizeOz = seed.sizeOz == null ? null : Number(seed.sizeOz);
      // Structured edits can retain an existing exact name field. New source
      // inputs keep their normal whitespace normalization by default.
      const names = settings.preserveNames === true && Array.isArray(seed.names) ? [...seed.names] : M.normalizeNames(seed.names ?? seed.name);
      const targetHeight = Number(seed.targetHeight ?? M.defaultHeightFor(templateFamily, productId, sizeOz));
      if (!names.length || names.length > M.maxNamesFor(templateFamily)) throw new RangeError('Enter 1–' + M.maxNamesFor(templateFamily) + ' names for this template.');
      if (names.some(name => typeof name !== 'string' || !name || name.length > M.MAX_NAME_LENGTH || M.unsupportedCharacters(name).length)) throw new RangeError('Enter names of 1–60 characters supported by Cookie, one name per line.');
      if (!Number.isFinite(targetHeight) || targetHeight < 20 || targetHeight > 200) throw new RangeError('Artwork height must be between 20 and 200 mm.');
      const factor = targetHeight / spec.imageWidth, board = M.boardForProduct(productId, spec.imageHeight * factor);
      const design = { version: 1, designType: 'family', fontEdition: M.FONT_EDITION, id: String(seed.id || 'family-design'), templateFamily, variant, names, name: names.join(', '),
        fontId: M.FONT_ID, productId, sizeOz, targetHeight, separator,
        ...(M.usesStanleyPanel(templateFamily, productId, sizeOz) ? { placementEdition: M.STANLEY_PANEL_EDITION } : {}), board, layers: {} };
      const horizontal = { icon: { x: 0, y: 0, width: spec.imageWidth, height: spec.imageHeight } };
      const fitted = fitNames(spec, names, separator);
      fitted.names.forEach((piece, index) => { horizontal['name' + index] = piece; });
      fitted.hearts.forEach((piece, index) => { horizontal['heart' + index] = piece; });
      const left = (board.width - spec.imageHeight * factor) / 2, top = (board.height - targetHeight) / 2;
      design.layers = Object.fromEntries(Object.entries(horizontal).map(([key, p]) => [key, {
        x: left + p.y * factor, y: top + (spec.imageWidth - p.x - p.width) * factor, width: p.height * factor, height: p.width * factor
      }]));
      const errors = validate(design); if (errors.length) throw new RangeError(errors.join(' ')); return design;
    }
    function validate(design) {
      const errors = M.validate(design); if (errors.length) return errors;
      if (!icons[design.variant]) return ['This family title is not loaded. Reload the matching product page.'];
      for (const key of M.layerKeys(design)) {
        const text = M.textForLayer(design, key), asset = M.assetForLayer(design, key), layer = design.layers[key];
        let aspect;
        try { aspect = M.shapeForLayer(design, key) ? 1 : text ? measure(text.text, text.fontId, text.angle).aspect : asset.imageHeight / asset.imageWidth; }
        catch (error) { errors.push(error.message); continue; }
        if (Math.abs(layer.width / layer.height / aspect - 1) > .001) errors.push('The ' + key + ' proportions have changed. Reset the layout to restore its original shape.');
      }
      // Hearts stay entirely inside the verified empty band and between the
      // visible bounds of neighboring names, so they cannot touch title ink,
      // divider rules or letters even when the descender fitter moves a name.
      const spec = M.TEMPLATES[design.variant], icon = design.layers.icon, factor = icon.height / spec.imageWidth;
      M.heartKeys(design).forEach((key, index) => {
        const heart = design.layers[key], before = design.layers['name' + index], after = design.layers['name' + (index + 1)];
        if (heart.x < icon.x + spec.opening.y * factor - .001 || heart.x + heart.width > icon.x + (spec.opening.y + spec.opening.height) * factor + .001 ||
            heart.y < after.y + after.height - .001 || heart.y + heart.height > before.y + .001) {
          errors.push('Keep each heart inside the title opening and between its neighboring names. Reset the layout to restore the spacing.');
        }
      });
      return errors;
    }
    function paintHeart(ctx) {
      // Normalized vector heart; no font or additional asset is required.
      ctx.beginPath(); ctx.moveTo(.5, 1);
      ctx.bezierCurveTo(.38, .82, 0, .56, 0, .25);
      ctx.bezierCurveTo(0, .10, .10, 0, .25, 0);
      ctx.bezierCurveTo(.36, 0, .45, .06, .5, .16);
      ctx.bezierCurveTo(.55, .06, .64, 0, .75, 0);
      ctx.bezierCurveTo(.90, 0, 1, .10, 1, .25);
      ctx.bezierCurveTo(1, .56, .62, .82, .5, 1);
      ctx.closePath(); ctx.fillStyle = '#000'; ctx.fill();
    }
    function drawLayer(ctx, design, key, scale = 1) {
      if (!Number.isFinite(scale) || scale <= 0 || !M.layerKeys(design).includes(key)) throw new RangeError('Choose a valid artwork piece and positive drawing scale.');
      const text = M.textForLayer(design, key), piece = design.layers[key]; ctx.save(); ctx.scale(scale, scale);
      if (M.shapeForLayer(design, key)) {
        ctx.translate(piece.x, piece.y + piece.height); ctx.rotate(-Math.PI / 2); ctx.scale(piece.height, piece.width); paintHeart(ctx);
      }
      else if (!text) { ctx.drawImage(icons[design.variant], piece.x, piece.y, piece.width, piece.height); }
      else {
        const m = measure(text.text, text.fontId, text.angle), factor = piece.width / m.width;
        ctx.translate(piece.x - m.yMin * factor, piece.y + m.xMax * factor); ctx.rotate(-Math.PI / 2); ctx.scale(factor, factor);
        fontStyle(ctx); ctx.fillText(text.text, 0, 0);
      }
      ctx.restore();
    }
    function paint(ctx, design, scale) { for (const key of M.layerKeys(design)) drawLayer(ctx, design, key, scale); }
    function resizeForHeight(design, height) {
      const errors = validate(design); if (errors.length) throw new RangeError(errors.join(' '));
      const targetHeight = Number(height); if (!Number.isFinite(targetHeight) || targetHeight < 20 || targetHeight > 200) throw new RangeError('Artwork height must be between 20 and 200 mm.');
      const updated = M.clone(design), bounds = M.bounds(design), factor = targetHeight / bounds.height;
      updated.targetHeight = targetHeight; updated.board = M.boardForProduct(updated.productId, bounds.width * factor);
      const left = (updated.board.width - bounds.width * factor) / 2, top = (updated.board.height - targetHeight) / 2;
      for (const key of M.layerKeys(design)) { const p = design.layers[key]; updated.layers[key] = { x: left + (p.x - bounds.x) * factor, y: top + (p.y - bounds.y) * factor, width: p.width * factor, height: p.height * factor }; }
      const invalid = validate(updated); if (invalid.length) throw new RangeError(invalid.join(' ')); return updated;
    }
    function resizeForProduct(design, productId, sizeOz = null) {
      if (!Object.hasOwn(M.PRODUCTS, productId)) throw new RangeError('Choose Owala or Stanley.');
      return createDesign({ ...design, productId, sizeOz, targetHeight: M.defaultHeightFor(design.templateFamily, productId, sizeOz) });
    }
    function reset(design) { return createDesign(design); }
    function fitInfo(design) {
      const bounds = M.bounds(design), automatic = createDesign(design);
      return { actualWidth: bounds.width, actualHeight: bounds.height, requestedHeight: design.targetHeight, widthLimited: false,
        fontGain: fitNames(M.TEMPLATES[design.variant], design.names, M.separatorFor(design)).fontGain, descenderOptimized: fitNames(M.TEMPLATES[design.variant], design.names, M.separatorFor(design)).descenderOptimized,
        edited: M.layerKeys(design).some(key => ['x', 'y', 'width', 'height'].some(field => Math.abs(design.layers[key][field] - automatic.layers[key][field]) > .001)) };
    }
    function thumbnail(design, width = 180, height = 280) {
      const output = canvas(width, height), bounds = M.bounds(design), scale = Math.min((width - 8) / bounds.width, (height - 8) / bounds.height), ctx = output.getContext('2d', { willReadFrequently: true });
      ctx.translate((width - bounds.width * scale) / 2 - bounds.x * scale, (height - bounds.height * scale) / 2 - bounds.y * scale); paint(ctx, design, scale); return output;
    }
    function assertValid(design) {
      const errors = validate(design); if (errors.length) throw new RangeError(errors.join(' '));
    }
    function productionSpec(design, dpi = DPI) {
      assertValid(design);
      if (!Number.isFinite(dpi) || dpi < 72 || dpi > 1200) throw new RangeError('Choose a DPI from 72 to 1200.');
      const { width, height } = design.board, scale = dpi / 25.4;
      return { physicalArea: { widthMm: width, heightMm: height }, region: { x: 0, y: 0, width, height },
        dpi, scale, pixelWidth: Math.round(width * scale), pixelHeight: Math.round(height * scale),
        filename: `family-${width}x${height}mm-${dpi}dpi.png` };
    }
    function renderCanvas(design, settings = {}) {
      assertValid(design);
      const scale = settings.scale ?? 4, color = settings.color ?? '#000';
      if (!Number.isFinite(scale) || scale <= 0 || scale > 1200 / 25.4) throw new RangeError('Choose a supported preview scale.');
      if (typeof color !== 'string' || !CSS.supports('color', color)) throw new RangeError('Choose a valid ink color.');
      const output = canvas(Math.max(1, Math.round(design.board.width * scale)), Math.max(1, Math.round(design.board.height * scale)));
      const ctx = output.getContext('2d', { willReadFrequently: true }); paint(ctx, design, scale);
      // Tint alpha only. Black production output and silver product previews
      // use identical outlines, tail shaping, icon normalization and geometry.
      ctx.globalCompositeOperation = 'source-in'; ctx.fillStyle = color;
      ctx.fillRect(0, 0, output.width, output.height); ctx.globalCompositeOperation = 'source-over';
      return output;
    }
    function place(design, settings = {}) {
      assertValid(design);
      const region = { ...settings.region }, physicalArea = { ...settings.physicalArea };
      const verticalPlacement = settings.verticalPlacement ?? 'center';
      if (![region.x, region.y, region.width, region.height, physicalArea.widthMm, physicalArea.heightMm].every(Number.isFinite) ||
          region.width <= 0 || region.height <= 0 || physicalArea.widthMm <= 0 || physicalArea.heightMm <= 0 ||
          !['center', 'optical'].includes(verticalPlacement)) throw new RangeError('A valid calibrated region and physical area are required.');
      const bounds = M.bounds(design), widthMm = bounds.width, heightMm = bounds.height;
      if (widthMm > physicalArea.widthMm + .001 || heightMm > physicalArea.heightMm + .001) {
        throw new RangeError('The artwork does not fit this physical engraving area.');
      }
      const verticalOffsetMm = verticalPlacement === 'optical' ? -Math.max(0, Math.min(10, (physicalArea.heightMm - heightMm) / 2)) : 0;
      const physicalPlacement = { x: physicalArea.widthMm / 2 - (bounds.xMin + bounds.xMax) / 2,
        y: physicalArea.heightMm / 2 - (bounds.yMin + bounds.yMax) / 2 + verticalOffsetMm };
      const xScale = region.width / physicalArea.widthMm, yScale = region.height / physicalArea.heightMm;
      const x = region.x + physicalPlacement.x * xScale, y = region.y + physicalPlacement.y * yScale;
      const mappedBounds = { xMin: x + bounds.xMin * xScale, yMin: y + bounds.yMin * yScale,
        xMax: x + bounds.xMax * xScale, yMax: y + bounds.yMax * yScale };
      return { version: 1, id: design.id, units: 'mm', board: { ...design.board }, region, physicalArea,
        widthMm, heightMm, verticalPlacement, verticalOffsetMm, physicalPlacement,
        physicalBounds: { xMin: bounds.xMin + physicalPlacement.x, yMin: bounds.yMin + physicalPlacement.y,
          xMax: bounds.xMax + physicalPlacement.x, yMax: bounds.yMax + physicalPlacement.y, widthMm, heightMm },
        placement: { x, y, xScale, yScale, transform: `translate(${x} ${y}) scale(${xScale} ${yScale})` },
        bounds: mappedBounds,
        fits: mappedBounds.xMin >= region.x - .001 && mappedBounds.yMin >= region.y - .001 &&
          mappedBounds.xMax <= region.x + region.width + .001 && mappedBounds.yMax <= region.y + region.height + .001 };
    }
    async function exportPng(design, settings = {}) {
      const spec = productionSpec(design);
      // A CPU-backed production surface avoids GPU glyph-cache timing changing
      // sparse edge alpha between the approved raster and a later export.
      const output = canvas(spec.pixelWidth, spec.pixelHeight), ctx = output.getContext('2d', { willReadFrequently: true });
      if (settings.verticalPlacement !== undefined) {
        const placement = place(design, { ...spec, verticalPlacement: settings.verticalPlacement });
        ctx.translate(placement.physicalPlacement.x * spec.scale, placement.physicalPlacement.y * spec.scale);
      }
      paint(ctx, design, spec.scale);
      const blob = await new Promise((resolve, reject) => output.toBlob(value => value ? resolve(value) : reject(new Error('PNG export failed.')), 'image/png'));
      return new Blob([withDpi(new Uint8Array(await blob.arrayBuffer()), DPI)], { type: 'image/png' });
    }
    function metadata(design) {
      assertValid(design);
      return { fontEdition: M.FONT_EDITION, template: M.variantFor(design).label, fontId: M.FONT_ID,
        templateFamily: design.templateFamily, names: [...design.names], separator: M.separatorFor(design),
        ...(design.placementEdition === undefined ? {} : { placementEdition: design.placementEdition, verticalPlacement: M.verticalPlacementFor(design) }),
        fonts: { cookie: { family: 'Cookie', weight: 400 } }, widthMm: M.bounds(design).width, heightMm: M.bounds(design).height, dpi: DPI };
    }
    function composition(design) { assertValid(design); return { design, bounds: M.bounds(design), layers: M.clone(design.layers) }; }
    return Object.freeze({ createDesign, fit: createDesign, measure, validate, paint, drawLayer, exportPng, resizeForHeight, resizeForProduct, reset, fitInfo, thumbnail, renderCanvas, productionSpec, place, metadata, composition, inkBounds: M.bounds,
      assetInfo: Object.freeze({ dpi: DPI, fonts: M.FONTS, icons: M.TEMPLATES, fontEdition: M.FONT_EDITION }) });
  }

  function crc32(bytes) { let crc = 0xffffffff; for (const byte of bytes) { crc ^= byte; for (let bit = 0; bit < 8; bit++) crc = (crc >>> 1) ^ ((crc & 1) ? 0xedb88320 : 0); } return (crc ^ 0xffffffff) >>> 0; }
  function withDpi(png, dpi) {
    if (!(png instanceof Uint8Array) || png.length < 33 || ![137,80,78,71,13,10,26,10].every((byte, i) => png[i] === byte)) throw new TypeError('A valid PNG is required.');
    const phys = new Uint8Array(21), view = new DataView(phys.buffer), density = Math.round(dpi / .0254);
    view.setUint32(0, 9); phys.set([112,72,89,115], 4); view.setUint32(8, density); view.setUint32(12, density); phys[16] = 1; view.setUint32(17, crc32(phys.subarray(4, 17)));
    const chunks = [png.subarray(0, 8)], input = new DataView(png.buffer, png.byteOffset, png.byteLength); let offset = 8, inserted = false, ended = false;
    while (offset + 12 <= png.length) {
      const length = input.getUint32(offset), end = offset + length + 12; if (end > png.length) throw new TypeError('The PNG has an incomplete chunk.');
      const type = String.fromCharCode(...png.subarray(offset + 4, offset + 8)); if (type !== 'pHYs') chunks.push(png.subarray(offset, end));
      if (type === 'IHDR') { chunks.push(phys); inserted = true; } offset = end; if (type === 'IEND') { ended = true; break; }
    }
    if (!inserted || !ended) throw new TypeError('The PNG is missing its header or end chunk.');
    const result = new Uint8Array(chunks.reduce((sum, chunk) => sum + chunk.length, 0)); let cursor = 0;
    for (const chunk of chunks) { result.set(chunk, cursor); cursor += chunk.length; } return result;
  }
  return Object.freeze({ load, create: load, withDpi });
});
